import { APP_STORE_APP_ID } from '@lib/constants';

/**
 * The App Store's own lookup API. Public and keyless; the UK storefront is
 * the one the ratings shown on the homepage and in its structured data refer
 * to.
 */
const APP_STORE_LOOKUP_URL = `https://itunes.apple.com/lookup?id=${APP_STORE_APP_ID}&country=gb`;

// A homepage regeneration must never wait on Apple: past this, the rating is
// left out rather than holding the build.
export const APP_STORE_LOOKUP_TIMEOUT_MS = 3000;

export type AppStoreRating = {
  /** Average star rating, 0 to 5. */
  ratingValue: number;
  ratingCount: number;
};

type AppStoreLookupResponse = {
  results?: {
    averageUserRating?: unknown;
    userRatingCount?: unknown;
  }[];
};

const isPositiveFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const parseAppStoreRating = (
  response: AppStoreLookupResponse | null | undefined
): AppStoreRating | null => {
  const app = response?.results?.[0];
  if (!app) return null;
  const { averageUserRating, userRatingCount } = app;
  if (
    !isPositiveFiniteNumber(averageUserRating) ||
    !isPositiveFiniteNumber(userRatingCount)
  ) {
    return null;
  }
  return { ratingValue: averageUserRating, ratingCount: userRatingCount };
};

/**
 * The app's live App Store rating, or null when Apple can't be reached or
 * answers with something unusable. Null hides the rating everywhere it would
 * show, so a stale or invented number is never published.
 */
export const fetchAppStoreRating = async (): Promise<AppStoreRating | null> => {
  // AbortController rather than AbortSignal.timeout: the latter is missing
  // from the jsdom test environment, and this works in both.
  const abortController = new AbortController();
  const timeoutHandle = setTimeout(
    () => abortController.abort(),
    APP_STORE_LOOKUP_TIMEOUT_MS
  );

  try {
    const response = await fetch(APP_STORE_LOOKUP_URL, {
      signal: abortController.signal
    });
    if (!response.ok) return null;
    return parseAppStoreRating(await response.json());
  } catch (error) {
    console.error('[app-store-rating] lookup failed', error);
    return null;
  } finally {
    clearTimeout(timeoutHandle);
  }
};

/** "4.6", the precision the stores themselves show. */
export const formatRatingValue = (ratingValue: number): string =>
  ratingValue.toFixed(1);
